import React, { Fragment, useState } from 'react';
import ReactMarkdown from 'markdown-to-jsx';
import { connect } from 'react-redux';
import InputMask from 'react-input-mask';
import { reduxForm, formValues, getFormSyncErrors, getFormMeta } from 'redux-form';

import Checkbox from '@ynap/checkbox';
import BEMHelper from '@ynap/bem-helper';
import Field, { Input } from '@ynap/field';
import PaymentCard from '@ynap/payment-card';
import { SimpleAsyncButton } from '@ynap/button';
import Notification, { Tooltip } from '@ynap/notification';
import { submit, onSubmitFail } from '@ynap/form-utils';

import validate from './validate';
import collateErrors from './collateErrors';

import { buildLabels, handleExpiryDateMask, getCreditCardMask, getFormErrors, getCardTypeFromValidator } from './creditCardFormHelper';

import { CreditCardValues, CreditCardFormProps, CVVProps, InputState, CardDetails } from './@types';

const bem = new BEMHelper('CreditCardForm');

export function CVV(props: CVVProps) {
    const { labels, className, showErrorNotification, initialValues } = props;
    const isEditing = initialValues?.lastFourDigits;

    return (
        <Field
            component={Input}
            name="cvv"
            id="cvvNumber"
            className={className}
            type="number"
            autoComplete="cc-csc"
            placeholder={isEditing ? '•••' : labels.cvv}
            showErrorNotification={showErrorNotification}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.cvvFormat || labels.cvv)}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.cvv)}
            disabled={isEditing}
        />
    );
}

export function CreditCardForm(props: CreditCardFormProps) {
    const {
        component: Component = 'form',
        form,
        error,
        toolTipError,
        loading,
        submitting = false,
        handleSubmit,
        onSubmit,
        messages,
        className,
        children,
        hideSaveCard = false,
        showCvvOnly,
        cardTypeInfo,
        synchronousErrors,
        formMeta,
        locale,
        config,
        isCardWallet,
        initialValues,
    } = props;

    const cardExpiryMask = '99/9999';
    const [cardExpiry, setCardExpiry] = useState<InputState>({ value: '', mask: cardExpiryMask });
    const [creditCardNumber, setCreditCardNumber] = useState<InputState>({
        value: '',
        mask: getCreditCardMask(''),
    });
    const [cardDetails, setCardDetails] = useState<CardDetails>(cardTypeInfo ? cardTypeInfo : getCardTypeFromValidator(creditCardNumber.value));

    const acceptedCCTypes = config?.head?.acceptedCCTypes || [];
    const isCardWalletModifier = isCardWallet ? 'isCardWallet' : '';
    const isEditing = initialValues?.lastFourDigits;

    const onChangeCardNumber = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        setCreditCardNumber({
            value,
            mask: getCreditCardMask(value),
        });

        setTimeout(() => setCardDetails(cardTypeInfo ? cardTypeInfo : getCardTypeFromValidator(value)));
    };

    const onChangeCardExpiry = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
        setCardExpiry({
            value,
            mask: cardExpiryMask,
        });

    const renderForm = () => {
        const labels = buildLabels(messages, cardDetails);
        const firstError = getFormErrors(formMeta, synchronousErrors, isCardWallet);

        return !showCvvOnly ? (
            <Fragment>
                <div className={bem('cardDetails', isCardWalletModifier)}>{labels.cardDetails}</div>
                <Field
                    name="cardHolderName"
                    component={Input}
                    className={bem('cardholder', isCardWalletModifier)}
                    autoComplete="cc-name"
                    placeholder={labels.cardHolderName}
                    type="text"
                    label={labels.cardHolderName}
                    showErrorNotification={false}
                />
                <Field
                    name="number"
                    component={InputMask}
                    value={creditCardNumber.value}
                    mask={creditCardNumber.mask}
                    onChange={onChangeCardNumber}
                    maskChar={null}
                    className={bem('number')}
                    placeholder={isEditing ? `•••• •••• •••• ${initialValues?.lastFourDigits}` : labels.number}
                    autoComplete="cc-number"
                    showErrorNotification={false}
                    label={labels.number}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.cardNumberFormat || labels.number)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.number)}
                    disabled={isEditing}
                />
                <div className={bem('cardNumberContainer')}>
                    <PaymentCard className={bem('cardType')} card={cardDetails.cardType?.replace('-', '').toLowerCase()} />
                </div>
                <div className={bem('cardAdditionalInfoContainer')}>
                    <Field
                        name="expiryDateStr"
                        value={cardExpiry.value}
                        mask={cardExpiry.mask}
                        onChange={onChangeCardExpiry}
                        maskChar={null}
                        component={InputMask}
                        className={bem('expiry', isCardWalletModifier)}
                        placeholder={labels.expiryDate}
                        autoComplete="cc-exp"
                        maxLength={5}
                        errorStyle="highlight"
                        showErrorNotification={false}
                        beforeMaskedValueChange={handleExpiryDateMask}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.expiryDateFormat || labels.expiryDate)}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => (e.target.placeholder = labels.expiryDate)}
                    />
                    <CVV
                        labels={labels}
                        // If needed, you can pass cardDetails and creditCardNumber too.
                        className={bem('cvv', isCardWalletModifier)}
                        showErrorNotification={false}
                        initialValues={initialValues}
                    />
                </div>

                {firstError && <Notification type="field-error" message={firstError} className={bem('cardErrors')} />}
            </Fragment>
        ) : (
            <CVV
                labels={labels}
                // Pass additional props if required.
                className={bem('cvv', ['confirmation', firstError?.includes('CVV') ? 'error' : ''])}
                showErrorNotification
            />
        );
    };

    return (
        // @ts-ignore-error
        <Component id={form} className={bem(null, { submitting }, className)} onSubmit={onSubmit && handleSubmit && handleSubmit(submit)}>
            {renderForm()}

            {isCardWallet && locale && (
                <>
                    <p className={bem('privacyPolicy')}>
                        <ReactMarkdown
                            options={{
                                overrides: {
                                    a: (props) => (
                                        <a className={bem('privacyPolicyLink')} href={props.href} target="_blank" rel="noopener noreferrer">
                                            {props.children}
                                        </a>
                                    ),
                                },
                            }}
                        >
                            {messages.creditCardForm.privacyPolicy({ locale: locale.id })}
                        </ReactMarkdown>
                    </p>

                    <h4 className={bem('acceptedCardsTitle')}>{messages.creditCardForm.acceptedCards()}</h4>

                    <ul className={bem('acceptedCardsList')}>
                        {acceptedCCTypes.map((type) => (
                            <PaymentCard className={bem('cardType', isCardWalletModifier)} card={type.replace('_', '').toLowerCase()} key={type} />
                        ))}
                    </ul>
                </>
            )}

            {!hideSaveCard && !showCvvOnly && (
                <Field
                    component={Checkbox}
                    name="saveCard"
                    className={bem('saveCard')}
                    format={(value: any) => value === 'true'}
                    normalize={(value: any) => value.toString()}
                >
                    {messages.creditCardForm.saveCard()}
                </Field>
            )}
            {typeof children === 'function' ? children(props) : children}
            {onSubmit && !showCvvOnly && (
                <Tooltip
                    className={bem('error', isCardWalletModifier)}
                    type="error"
                    message={error || (toolTipError && messages.creditCardForm.error?.(toolTipError)) || ''}
                    defaultOpen={!submitting && (!!error || !!toolTipError)}
                    key={String(submitting || error || toolTipError)}
                >
                    <SimpleAsyncButton
                        type="primary"
                        className={bem('submit')}
                        loading={loading || submitting}
                        copy={messages.creditCardForm.submit ? messages.creditCardForm.submit() : ''}
                    />
                </Tooltip>
            )}
        </Component>
    );
}

const mapStateToProps = (state: any, props: any) => ({
    synchronousErrors: getFormSyncErrors(props.isCardWallet ? 'creditCard' : 'paymentOptions')(state),
    formMeta: getFormMeta(props.isCardWallet ? 'creditCard' : 'paymentOptions')(state),
});

const CreditCardFormWithConnect = connect(mapStateToProps)(CreditCardForm);

export const CreditCardFormWithValues = formValues<{ numberValue: string }, any>({ numberValue: 'number' })(CreditCardFormWithConnect);

export default reduxForm<CreditCardValues, CreditCardFormProps>({
    form: 'creditCard',
    validate,
    // TODO collateErrors is not a valid type
    // @ts-ignore
    collateErrors,
    onSubmitFail,
    // @ts-ignore
})(CreditCardFormWithValues);
